import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '@obis/firebase-config';
import { 
  CheckCircleIcon, ArrowLeftIcon, ArrowRightIcon, 
  AcademicCapIcon, BuildingLibraryIcon, BuildingOfficeIcon, FaceSmileIcon, CheckIcon
} from '@heroicons/react/24/solid';

const STEPS = [
  { id: 'identity', label: 'Basic Identity', description: 'Name, motto, and type' },
  { id: 'contact', label: 'Contact & Location', description: 'Address and phone' },
  { id: 'regional', label: 'Regional Settings', description: 'Currency and timezone' },
  { id: 'academic', label: 'Academic Setup', description: 'Terms and structure' },
  { id: 'branding', label: 'Branding', labelShort: 'Design & Colors' }
];

const SchoolProfileSetup: React.FC = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const id = tenantSlug || 'default-tenant';
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Form State (Optimized with Smart GTM Defaults for India)
  const [formData, setFormData] = useState({
    schoolName: '',
    motto: '',
    schoolType: 'K-12 Academy',
    email: '',
    phone: '',
    address: '',
    country: 'India',
    currency: 'INR',
    timezone: 'IST',
    academicYearStart: 'april',
    gradingSystem: 'percentage',
    primaryColor: '#3b82f6',
    logoUrl: ''
  });

  useEffect(() => {
    // Load existing data if they already started
    const fetchProfile = async () => {
      try {
        const docRef = doc(db, 'tenants', id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setFormData(prev => ({ ...prev, ...snap.data() }));
        }
      } catch (err) {
        console.error("Error fetching existing profile", err);
      }
    };
    fetchProfile();
  }, [id]);

  useEffect(() => {
     if (saveStatus === 'saved') {
        const timer = setTimeout(() => setSaveStatus('idle'), 2500);
        return () => clearTimeout(timer);
     }
  }, [saveStatus]);

  const handleNext = async () => {
    setIsSaving(true);
    setSaveStatus('saving');
    try {
      // Auto-save step data to "tenants" collection
      await setDoc(doc(db, 'tenants', id), formData, { merge: true });
      
      if (currentStep < STEPS.length - 1) {
        setSaveStatus('saved');
        setCurrentStep(prev => prev + 1);
      } else {
        // Final step completion logic
        const obRef = doc(db, 'onboarding', id);
        const obSnap = await getDoc(obRef);
        if (obSnap.exists()) {
          const obData = obSnap.data();
          const currentProgress = obData.progressPercentage || 0;
          const stepsData = obData.steps || {};
          
          if (!stepsData.profile) {
              const newProgress = Math.min(100, currentProgress + 16.6); // 1/6 completion roughly
              await updateDoc(obRef, {
                'steps.profile': true,
                progressPercentage: Math.round(newProgress)
              });
          }
        } else {
           await setDoc(obRef, {
             steps: {
               profile: true, students: false, configureFees: false, inviteTeachers: false, firstAttendance: false, firstFee: false
             },
             progressPercentage: 17
           });
        }
        
        navigate(`/app/${id}`);
      }
    } catch (err) {
      console.error("Error saving step", err);
      setSaveStatus('idle');
    } finally {
      setIsSaving(false);
    }
  };

  const currentStepData = STEPS[currentStep];
  const percentComplete = Math.round(((currentStep + 1) / STEPS.length) * 100);

  const renderInstitutionIcon = () => {
      switch(formData.schoolType) {
         case 'High School': return <BuildingLibraryIcon className="w-48 h-48 text-white" />;
         case 'Primary School': return <FaceSmileIcon className="w-48 h-48 text-white" />;
         case 'University / College': return <BuildingOfficeIcon className="w-48 h-48 text-white" />;
         default: return <AcademicCapIcon className="w-48 h-48 text-white" />;
      }
  };

  return (
    <div className="space-y-6 animate-fade-in-up mt-2 w-full mx-auto transform scale-[0.92] origin-top">
      {/* Header with Title + "Why this matters" & Progress % Indicator */}
      <div className="flex items-start justify-between mb-6 border-b border-gray-200 pb-5">
        <div className="flex-1">
           <button onClick={() => navigate(`/app/${id}`)} className="text-gray-400 hover:text-blue-600 mb-2 flex items-center gap-1 text-sm font-bold transition-colors">
              <ArrowLeftIcon className="w-4 h-4" /> Back to Launch Setup
           </button>
           <h2 className="text-3xl font-black text-gray-800 tracking-tight">🏢 Create Your School</h2>
           <p className="text-emerald-700 text-sm mt-1.5 font-bold bg-emerald-50 inline-block px-3 py-1 rounded-lg">
             This will configure your school's operating system across all modules.
           </p>
        </div>
        
        <div className="text-right flex flex-col items-end justify-center pt-5 pl-8">
           <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-2">
              {saveStatus === 'saved' && <span className="text-emerald-500 flex items-center gap-1 animate-fade-in"><CheckIcon className="w-3 h-3" /> Auto-saved</span>}
              <span>Step {currentStep + 1} of {STEPS.length}</span>
           </div>
           <div className="bg-blue-50 px-4 py-1.5 rounded-xl border border-blue-100 flex items-center gap-2">
             <div className="text-xl font-black text-blue-600 tracking-tight leading-none">{percentComplete}%</div>
             <div className="text-xs font-bold text-blue-700 leading-tight">Setup<br/>Complete</div>
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        
        {/* LEFT: Progress Sidebar (3 cols) */}
        <div className="lg:col-span-3 bg-white rounded-2xl p-6 shadow-sm border border-gray-200 flex flex-col h-full">
           <h3 className="text-sm font-bold text-gray-800 mb-8 uppercase tracking-wider">Setup Progress</h3>
           
           <div className="space-y-8 relative before:absolute before:inset-0 before:ml-[11px] before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-gray-200 before:to-gray-100">
             {STEPS.map((step, index) => {
               const isActive = index === currentStep;
               const isCompleted = index < currentStep;
               
               return (
                 <div key={step.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                   <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-white bg-slate-50 text-slate-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10"
                        style={{ backgroundColor: isActive ? '#3b82f6' : isCompleted ? '#10b981' : 'white', color: isActive || isCompleted ? 'white' : '#94a3b8' }}>
                       {isCompleted ? <CheckCircleIcon className="w-4 h-4" /> : <span className="text-[10px] font-black">{index + 1}</span>}
                   </div>
                   
                   <div className={`w-[calc(100%-2.5rem)] md:w-[calc(50%-2.5rem)] text-left ${isActive ? 'opacity-100' : 'opacity-60'}`}>
                      <h4 className={`text-xs font-bold ${isActive ? 'text-blue-700' : isCompleted ? 'text-emerald-600' : 'text-gray-500'}`}>{step.label}</h4>
                   </div>
                 </div>
               )
             })}
           </div>
        </div>

        {/* CENTER: Dynamic Form Wizard (6 cols) */}
        <div className="lg:col-span-6 bg-white rounded-2xl p-8 shadow-sm border border-gray-200 flex flex-col min-h-[450px]">
           <div className="flex items-center gap-3 mb-6 border-b border-gray-50 pb-4">
             <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-sm">{currentStep + 1}</div>
             <h3 className="text-xl font-bold text-gray-800">{currentStepData.label}</h3>
           </div>
           
           <div className="flex-1 space-y-7">
             {currentStep === 0 && (
               <div className="animate-fade-in-up space-y-6">
                 <div>
                   <label className="block text-sm font-bold text-gray-800 mb-1">Institution Name</label>
                   <p className="text-[11px] text-gray-500 mb-2">This will appear across all core platforms, reports, and certificates.</p>
                   <input type="text" value={formData.schoolName} onChange={(e) => setFormData({...formData, schoolName: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800" placeholder="e.g. Olive Bloom International School" />
                 </div>
                 <div>
                   <label className="block text-sm font-bold text-gray-800 mb-1">Motto / Tagline</label>
                   <p className="text-[11px] text-gray-500 mb-2">Optional — utilized on parent-facing portals and official branding.</p>
                   <input type="text" value={formData.motto} onChange={(e) => setFormData({...formData, motto: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800" placeholder="e.g. Empowering Leaders of Tomorrow" />
                 </div>
                 <div>
                   <label className="block text-sm font-bold text-gray-800 mb-1">Institution Type</label>
                   <p className="text-[11px] text-gray-500 mb-2">Determines default system hierarchy recommendations.</p>
                   <select value={formData.schoolType} onChange={(e) => setFormData({...formData, schoolType: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800">
                     <option>K-12 Academy</option>
                     <option>High School</option>
                     <option>Primary School</option>
                     <option>University / College</option>
                   </select>
                 </div>
               </div>
             )}

             {currentStep === 1 && (
               <div className="animate-fade-in-up space-y-6">
                 <div>
                   <label className="block text-sm font-bold text-gray-800 mb-1">Primary Email</label>
                   <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800" placeholder="admin@school.edu" />
                 </div>
                 <div>
                   <label className="block text-sm font-bold text-gray-800 mb-1">Phone Number</label>
                   <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800" placeholder="+91 (00) 0000-0000" />
                 </div>
                 <div>
                   <label className="block text-sm font-bold text-gray-800 mb-1">Full Physical Address</label>
                   <textarea rows={3} value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800" placeholder="123 Education Lane..." />
                 </div>
               </div>
             )}

             {currentStep === 2 && (
               <div className="animate-fade-in-up space-y-6">
                 <div>
                   <label className="block text-sm font-bold text-gray-800 mb-1">Country</label>
                   <p className="text-[11px] text-gray-500 mb-2">This drives backend regional logic, pre-configured for India GTM.</p>
                   <input type="text" value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800" />
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-bold text-gray-800 mb-1">Default Currency</label>
                     <select value={formData.currency} onChange={(e) => setFormData({...formData, currency: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800">
                       <option value="INR">INR (₹)</option>
                       <option value="USD">USD ($)</option>
                       <option value="EUR">EUR (€)</option>
                     </select>
                   </div>
                   <div>
                     <label className="block text-sm font-bold text-gray-800 mb-1">Timezone</label>
                     <select value={formData.timezone} onChange={(e) => setFormData({...formData, timezone: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800">
                       <option value="IST">Indian Standard Time (IST)</option>
                       <option value="UTC">UTC Standard</option>
                     </select>
                   </div>
                 </div>
               </div>
             )}

             {currentStep === 3 && (
               <div className="animate-fade-in-up space-y-6">
                 <div>
                   <label className="block text-sm font-bold text-gray-800 mb-1">Academic Year Start Month</label>
                   <p className="text-[11px] text-gray-500 mb-2">India regions natively follow an April-March cycle.</p>
                   <select value={formData.academicYearStart} onChange={(e) => setFormData({...formData, academicYearStart: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800">
                     <option value="april">April</option>
                     <option value="september">September</option>
                     <option value="january">January</option>
                   </select>
                 </div>
                 <div>
                   <label className="block text-sm font-bold text-gray-800 mb-1">Grading System Blueprint</label>
                   <p className="text-[11px] text-gray-500 mb-2">Percentage is standard for CBSE format structures.</p>
                   <select value={formData.gradingSystem} onChange={(e) => setFormData({...formData, gradingSystem: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800">
                     <option value="percentage">Percentage (0-100%) [CBSE Native]</option>
                     <option value="GPA">4.0 GPA Standard</option>
                     <option value="ib">IB Scale (1-7)</option>
                   </select>
                 </div>
               </div>
             )}

             {currentStep === 4 && (
               <div className="animate-fade-in-up space-y-6">
                 <div>
                   <label className="block text-sm font-bold text-gray-800 mb-1">Brand Primary Color</label>
                   <div className="flex items-center gap-3">
                     <input type="color" value={formData.primaryColor} onChange={(e) => setFormData({...formData, primaryColor: e.target.value})} className="w-12 h-12 border-0 rounded cursor-pointer" />
                     <input type="text" value={formData.primaryColor} onChange={(e) => setFormData({...formData, primaryColor: e.target.value})} className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800 uppercase" />
                   </div>
                 </div>
                 <div>
                   <label className="block text-sm font-bold text-gray-800 mb-1">School Logo URL</label>
                   <input type="url" value={formData.logoUrl} onChange={(e) => setFormData({...formData, logoUrl: e.target.value})} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all font-medium text-gray-800" placeholder="https://..." />
                 </div>
                 
                 <div className="mt-8 p-4 bg-emerald-50 rounded-xl border border-emerald-200 shadow-sm animate-pulse">
                    <p className="text-xs text-emerald-800 font-bold leading-relaxed flex items-start gap-3">
                      <CheckCircleIcon className="w-6 h-6 flex-shrink-0 text-emerald-500" />
                      <span>This is the final step! Clicking "Complete Setup" will mark your identity as complete and advance your go-live tracker.</span>
                    </p>
                 </div>
               </div>
             )}
           </div>

           {/* Action Bar */}
           <div className="mt-10 pt-5 border-t border-gray-100 flex items-center justify-between">
              {currentStep > 0 ? (
                <button 
                  onClick={() => setCurrentStep(prev => prev - 1)}
                  disabled={isSaving}
                  className="px-5 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  <ArrowLeftIcon className="w-4 h-4" /> Back
                </button>
              ) : (
                <div></div>
              )}

              <button 
                onClick={handleNext}
                disabled={isSaving}
                className="px-8 py-3.5 bg-gray-900 text-white font-bold text-sm flex items-center gap-2 rounded-xl shadow hover:bg-black hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-70"
              >
                {isSaving ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : currentStep === STEPS.length - 1 ? (
                  <>Complete Setup <CheckCircleIcon className="w-5 h-5 text-emerald-400" /></>
                ) : (
                  <>Save & Continue <ArrowRightIcon className="w-5 h-5 text-gray-300" /></>
                )}
              </button>
           </div>
        </div>

        {/* RIGHT: Visual Preview (3 cols) */}
        <div className="lg:col-span-3 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-2xl p-6 shadow-md border border-slate-800 flex flex-col text-white relative overflow-hidden group">
          {/* Subtle bg art */}
          <div className="absolute top-0 right-0 -mr-6 -mt-6 opacity-10 group-hover:scale-105 transition-transform duration-700">
             {renderInstitutionIcon()}
          </div>
          
          <div className="flex items-center justify-between mb-8 relative z-10 w-full border-b border-indigo-800/50 pb-3">
            <h3 className="text-xs font-bold text-blue-200 uppercase tracking-widest">Live Identity Preview</h3>
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
          </div>
          
          <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4 z-10 w-full">
             {/* Logo Builder */}
             <div className="w-24 h-24 rounded-2xl flex items-center justify-center shadow-2xl border-4 border-white/10 overflow-hidden bg-white group-hover:shadow-blue-900/50 transition-shadow duration-500" style={{ backgroundColor: formData.logoUrl ? 'white' : formData.primaryColor }}>
               {formData.logoUrl ? (
                 <img src={formData.logoUrl} className="max-w-full max-h-full object-contain" alt="School Logo" />
               ) : (
                 <span className="text-4xl font-black text-white">{formData.schoolName ? formData.schoolName.charAt(0) : 'O'}</span> 
               )}
             </div>
             
             {/* Dynamic Text */}
             <div className="pt-2 px-2">
               <h4 className="text-xl font-bold text-white tracking-tight leading-tight drop-shadow-md">
                  {formData.schoolName || 'Your School Name'}
               </h4>
               {formData.motto && (
                  <p className="text-xs text-blue-200 mt-2 italic font-medium opacity-90">
                    "{formData.motto}"
                  </p>
               )}
             </div>
             
             {/* Badges */}
             <div className="w-full pt-6 mt-4 border-t border-indigo-800/50 flex items-center justify-center gap-6">
               <div className="text-center">
                  <div className="text-[9px] uppercase tracking-wider text-blue-300/70 mb-1">Region</div>
                  <div className="text-xs font-semibold text-blue-50 bg-blue-900/40 px-2 py-0.5 rounded shadow-inner">{formData.country}</div>
               </div>
               <div className="w-px h-6 bg-indigo-800/50"></div>
               <div className="text-center">
                  <div className="text-[9px] uppercase tracking-wider text-blue-300/70 mb-1">Currency</div>
                  <div className="text-xs font-semibold text-emerald-50 bg-emerald-900/40 px-2 py-0.5 rounded shadow-inner">{formData.currency}</div>
               </div>
             </div>
          </div>

          <div className="mt-8 text-center relative z-10 w-full pt-4">
             <p className="text-[10px] text-blue-200/50 font-medium">As configured, this will sync directly to all localized systems instantly upon completion.</p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SchoolProfileSetup;
